import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Send, Loader2 } from "lucide-react";
import PageBanner from "../components/PageBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill out every field before sending.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Our team will reply from info@tmbglobal.news.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageBanner
        overline="Contact us"
        title="Let's build connected systems together."
        subtitle="Partnerships, media, programs, or general inquiries — reach our team and we will route your message to the right place."
        image="https://static.prod-images.emergentagent.com/jobs/34a752f1-0d81-4162-98d9-466f130466da/images/241396fa1857bef64b589bbf9b20a0eac09ec6cd9dedd9f9485275207579f472.png"
      />

      <section className="section">
        <div className="container-tmb grid md:grid-cols-12 gap-10">
          {/* Info */}
          <div className="md:col-span-5 space-y-6">
            <div>
              <div className="overline mb-3">Reach us</div>
              <h2 className="font-display text-4xl md:text-5xl text-[#1A2421] tracking-tight leading-[1.05] mb-4">
                Talk to TMB Global.
              </h2>
              <p className="text-[#4A5D54] leading-relaxed">
                Use the form to send a message — it goes directly to our central inbox at
                info@tmbglobal.news.
              </p>
            </div>

            <div className="card-tmb p-7 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#C85A3A]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-1">
                    Email
                  </div>
                  <a
                    href="mailto:info@tmbglobal.news"
                    data-testid="contact-email-link"
                    className="text-[#1A2421] hover:text-[#C85A3A]"
                  >
                    info@tmbglobal.news
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#C85A3A]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-1">
                    Detroit, USA
                  </div>
                  <div className="text-[#1A2421] leading-relaxed">
                    TMB Global Network<br />
                    2722 Second Street, Ste. G-2<br />
                    Detroit, MI 48201
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C85A3A]/10 flex items-center justify-center shrink-0">
                  <MapPin className="w-4 h-4 text-[#C85A3A]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-1">
                    Sandton, South Africa
                  </div>
                  <div className="text-[#1A2421] leading-relaxed">
                    155 West Street<br />
                    Sandton, Johannesburg<br />
                    Gauteng 2126
                  </div>
                </div>
              </div>
            </div>

            <div className="card-tmb p-7 bg-[#2C4C3B] text-white border-transparent">
              <div className="text-[#D99A29] uppercase tracking-[0.2em] font-bold text-xs mb-3">
                Direct emails
              </div>
              <ul className="space-y-2 text-sm text-white/90">
                <li><span className="text-white/60">CEO:</span> <a href="mailto:Emckenzie@tmbglobal.news" className="hover:text-white">Emckenzie@tmbglobal.news</a></li>
                <li><span className="text-white/60">Business Dev:</span> <a href="mailto:Hali@tmbglobal.news" className="hover:text-white">Hali@tmbglobal.news</a></li>
                <li><span className="text-white/60">Marketing:</span> <a href="mailto:JMorris@tmbglobal.news" className="hover:text-white">JMorris@tmbglobal.news</a></li>
                <li><span className="text-white/60">Donations:</span> <a href="mailto:Donate@tmbglobal.news" className="hover:text-white">Donate@tmbglobal.news</a></li>
              </ul>
            </div>
          </div>

          {/* Form */}
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="md:col-span-7 card-tmb p-8 md:p-10"
          >
            <div className="overline mb-3">Send a message</div>
            <h3 className="font-display text-3xl md:text-4xl text-[#1A2421] tracking-tight leading-tight mb-2">
              Tell us how we can help.
            </h3>
            <p className="text-sm text-[#4A5D54] mb-7">
              Your message is delivered to <strong>info@tmbglobal.news</strong>.
            </p>

            <div className="grid md:grid-cols-2 gap-5 mb-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
                >
                  Your name
                </label>
                <input
                  id="name"
                  type="text"
                  className="input-tmb"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Doe"
                  data-testid="contact-input-name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="input-tmb"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  data-testid="contact-input-email"
                />
              </div>
            </div>

            <div className="mb-5">
              <label
                htmlFor="subject"
                className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
              >
                Subject
              </label>
              <input
                id="subject"
                type="text"
                className="input-tmb"
                value={form.subject}
                onChange={update("subject")}
                placeholder="Partnership, programs, media…"
                data-testid="contact-input-subject"
              />
            </div>

            <div className="mb-7">
              <label
                htmlFor="message"
                className="block text-xs uppercase tracking-[0.15em] font-bold text-[#4A5D54] mb-2"
              >
                Message
              </label>
              <textarea
                id="message"
                rows={6}
                className="input-tmb resize-none"
                value={form.message}
                onChange={update("message")}
                placeholder="Share a few details about your inquiry…"
                data-testid="contact-input-message"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-button"
              className="btn-primary disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                </>
              ) : (
                <>
                  Send message <Send className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
